<!--
Provides a Recaptcha v2 element

Docs: https://developers.google.com/recaptcha
 -->
<script setup lang="ts">
import { useFormInput } from '@consumer/services/form'
import { VueRecaptcha } from 'vue-recaptcha'
import { union } from '@shared/array'
import { presence, isPresent } from '@shared/object'
import { usePage } from '@consumer/composables/sharedData'
import { isTest } from '@shared/environment'

type RecaptchaProps = { recaptchaInvisibleSiteKey: string; recaptchaCheckboxSiteKey: string }

const { props } = $(usePage<RecaptchaProps>())

const { recaptchaInvisibleSiteKey, recaptchaCheckboxSiteKey } = $(props)

const { field = 'recaptcha' } = defineProps<{
  // The name of the field, which will be used to retrieve a value from a parent form.
  field?: string
}>()

const { form, formData } = useFormInput(field)

const slots = defineSlots()
const invisible = $computed(() => Boolean(slots.default))

// Errors that were gathered from the input validations.
const validationErrors = $computed(() =>
  invisible || isPresent(formData.value)
    ? []
    : ['Please complete the reCAPTCHA.'],
)

watch(() => validationErrors.length, (hasErrors) => {
  if (hasErrors)
    form.onInputErrors(field, validationErrors)
  else
    form.onInputValid(field)
}, { immediate: true })

const errors = $computed(() => form.displayErrors.value && presence(union(validationErrors, formData.serverErrors)))

const recaptchaEl = $ref<VueRecaptcha>()

function resetRecaptcha () {
  try {
    recaptchaEl?.reset()
  }
  catch { } // NOTE: We will notify the user of this error using a flash message.
}

watchEffect(() => {
  if (errors)
    resetRecaptcha()
})

function clearRecaptcha () {
  form.onInputValueChange(field, '')
}

// NOTE: Reset the recaptcha whenever the form is done submitting.
watch(form.submitting, (submitting) => {
  if (!submitting) resetRecaptcha()
}, { flush: 'sync' })

const listeners = {
  async verify (value: string) {
    form.onInputValueChange(field, value)

    if (invisible) return await form.submit()
  },
  expired: clearRecaptcha,
  error: clearRecaptcha,
}

defineOptions({
  inheritAttrs: false,
})
</script>

<template>
  <div v-if="!isTest && !invisible" class="form-input">
    <VueRecaptcha ref="recaptchaEl" :sitekey="recaptchaCheckboxSiteKey" v-on="listeners"/>
    <V2InputError v-if="errors" class="mt-1.5 -mb-2" :size="form.size || 'small'" :errors="errors"/>
  </div>
  <GInput v-else-if="isTest && !invisible" :field="field" :label="false" required/>
  <slot v-else-if="isTest"/>
  <VueRecaptcha v-else ref="recaptchaEl" :sitekey="recaptchaInvisibleSiteKey" v-on="listeners">
    <slot/>
  </VueRecaptcha>
</template>
